import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchCityLeaderboard,
  type LeaderboardEntry,
  type LeaderboardPeriod,
} from '../socialApi';
import { pushPublicProfilePath } from '../shellHistory';
import { Card } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { UserAvatar } from './UserAvatar';

type CityLeaderboardCardProps = {
  cityId: string;
  onOpenUser: (userId: string) => void;
  limit?: number;
  mobileSwitch?: boolean;
};

function LeaderboardSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="pulse-leaderboard-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => <Skeleton key={index} height={52} />)}
    </div>
  );
}

export function CityLeaderboardCard({ cityId, onOpenUser, limit = 10, mobileSwitch = false }: CityLeaderboardCardProps) {
  const [period, setPeriod] = useState<LeaderboardPeriod>('all');
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(false);
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    if (!cityId) return;
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError(false);
    try {
      const response = await fetchCityLeaderboard(cityId, period, limit);
      if (requestId !== requestSequence.current) return;
      setEntries(response.data);
    } catch {
      if (requestId !== requestSequence.current) return;
      setError(true);
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [cityId, limit, period]);

  useEffect(() => { void load(); }, [load]);

  const openUser = (userId: string) => {
    pushPublicProfilePath(userId);
    onOpenUser(userId);
  };

  const periodTabs = (
    <div className={mobileSwitch ? 'pulse-ranking-period-tabs' : 'pulse-segmented pulse-leaderboard-tabs'} role="tablist" aria-label="Период рейтинга">
      <button type="button" role="tab" aria-selected={period === 'all'} className={period === 'all' ? 'is-active' : ''} onClick={() => setPeriod('all')}>Город</button>
      <button type="button" role="tab" aria-selected={period === 'week'} className={period === 'week' ? 'is-active' : ''} onClick={() => setPeriod('week')}>Неделя</button>
    </div>
  );

  return (
    <Card className={`pulse-leaderboard-card${mobileSwitch ? ' is-mobile-switch' : ''}`} aria-label="Рейтинг гостей">
      <div className={`pulse-side-heading${mobileSwitch ? ' pulse-mobile-leaderboard-heading' : ''}`}>
        <h2>Рейтинг гостей</h2>
        {mobileSwitch && periodTabs}
      </div>

      {!mobileSwitch && periodTabs}

      {!hasLoadedOnce && entries.length === 0 && loading ? <LeaderboardSkeleton rows={Math.min(limit, 6)} /> : error ? (
        <div className="pulse-side-empty"><strong>Рейтинг пока не загрузился</strong><button type="button" onClick={() => void load()}>Повторить</button></div>
      ) : entries.length === 0 ? (
        <div className="pulse-side-empty" aria-busy={loading}>Пока нет подтверждённых XP в этом городе.</div>
      ) : (
        <div className="pulse-leaderboard-list" aria-busy={loading}>
          {entries.slice(0, limit).map((entry) => (
            <button className={`pulse-leaderboard-row${entry.rank <= 3 ? ` is-top is-top-${entry.rank}` : ''}`} type="button" key={entry.user.id} onClick={() => openUser(entry.user.id)}>
              <span className="pulse-leaderboard-rank">{entry.rank}</span>
              <UserAvatar displayName={entry.user.display_name} avatarUrl={entry.user.avatar_url} shape="rounded-square" size={mobileSwitch ? 32 : 40} />
              <span className="pulse-leaderboard-identity">
                <strong>{entry.user.display_name?.trim() || 'Гость Хоппера'}</strong>
                {!mobileSwitch && <small>{entry.xp.toLocaleString('ru-RU')} XP · {entry.visits_count} визитов</small>}
              </span>
              {mobileSwitch && <small className="pulse-leaderboard-stats">{entry.xp.toLocaleString('ru-RU')} XP · {entry.visits_count} визитов</small>}
            </button>
          ))}
        </div>
      )}

      {!mobileSwitch && (
        <button className="pulse-leaderboard-all" type="button" disabled>
          Смотреть весь рейтинг
          <span>Скоро</span>
        </button>
      )}
    </Card>
  );
}
